"use client";

/**
 * AddToListButton — reusable "add to saved list" affordance (P21 C4).
 *
 * Props: { variantId: string; defaultQty?: number }
 *
 * On open: GET /v1/storefront/me/saved-lists to list the customer's lists.
 * User picks a list (or creates a new one inline), then POST
 * saved-lists/:id/items → success/danger toast.
 *
 * Uses a portal-rendered popover panel so it escapes any parent
 * overflow-hidden container (e.g. ProductCard tile clipping).
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button, useToast } from "@wmart/ui";
import type { SavedListDTO } from "@wmart/shared-types";
import { clientApi, ApiError } from "../lib/api-client";

export interface AddToListButtonProps {
  variantId: string;
  defaultQty?: number;
}

export function AddToListButton({ variantId, defaultQty = 1 }: AddToListButtonProps) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<SavedListDTO[]>([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [addingToId, setAddingToId] = useState<string | null>(null);

  // Inline create state within popover
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [createBusy, setCreateBusy] = useState(false);

  // Portal panel position (fixed, viewport-relative). Init off-screen so the
  // first paint isn't at the viewport top-left corner before recomputePos runs.
  const [panelPos, setPanelPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });

  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Compute panel position from the trigger's bounding rect
  const recomputePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const panelWidth = 256; // w-64 = 16rem = 256px
    let left = rect.left;
    // Clamp so panel doesn't overflow the right edge of the viewport
    if (left + panelWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - panelWidth - 8);
    }
    setPanelPos({ top: rect.bottom + 4, left });
  }, []);

  // Fetch lists when popover opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setListsLoaded(false);
    setShowCreate(false);
    setNewName("");
    recomputePos();
    clientApi
      .get<SavedListDTO[]>("/v1/storefront/me/saved-lists")
      .then((res) => {
        if (cancelled) return;
        setLists(res);
        setListsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setListsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, recomputePos]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    // Capture-phase so we catch scroll events from any ancestor
    document.addEventListener("scroll", recomputePos, true);
    window.addEventListener("resize", recomputePos);
    return () => {
      document.removeEventListener("scroll", recomputePos, true);
      window.removeEventListener("resize", recomputePos);
    };
  }, [open, recomputePos]);

  // Close on click-outside or Escape
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function addToList(listId: string) {
    setAddingToId(listId);
    try {
      await clientApi.post<SavedListDTO>(
        `/v1/storefront/me/saved-lists/${listId}/items`,
        { variant_id: variantId, quantity: defaultQty },
      );
      toast.push({ tone: "success", title: "Added to list" });
      setOpen(false);
    } catch (err) {
      toast.push({
        tone: "danger",
        title: "Could not add to list",
        body: err instanceof ApiError
          ? (err.problem?.detail ?? err.problem?.title ?? `HTTP ${err.status}`)
          : err instanceof Error ? err.message : "Try again",
      });
    } finally {
      setAddingToId(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    try {
      const created = await clientApi.post<SavedListDTO>(
        "/v1/storefront/me/saved-lists",
        { name },
      );
      // Add the variant to the newly created list
      await clientApi.post<SavedListDTO>(
        `/v1/storefront/me/saved-lists/${created.id}/items`,
        { variant_id: variantId, quantity: defaultQty },
      );
      toast.push({ tone: "success", title: "Added to new list", body: created.name });
      setOpen(false);
    } catch (err) {
      toast.push({
        tone: "danger",
        title: "Could not create list",
        body: err instanceof ApiError
          ? (err.problem?.detail ?? err.problem?.title ?? `HTTP ${err.status}`)
          : err instanceof Error ? err.message : "Try again",
      });
    } finally {
      setCreateBusy(false);
    }
  }

  const panel = (
    <div
      ref={panelRef}
      role="listbox"
      aria-label="Saved lists"
      style={{ position: "fixed", top: panelPos.top, left: panelPos.left }}
      className="z-50 w-64 rounded-sm border border-ink-200 bg-white shadow-lg"
    >
      <div className="flex flex-col gap-1 p-2 max-h-64 overflow-y-auto">
        {!listsLoaded ? (
          <div className="flex flex-col gap-2 p-2">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="h-8 animate-pulse rounded-sm bg-paper-warm"
                aria-hidden
              />
            ))}
          </div>
        ) : lists.length === 0 && !showCreate ? (
          <p className="px-2 py-1 text-body-sm text-ink-500">No lists yet.</p>
        ) : (
          lists.map((list) => (
            <button
              key={list.id}
              type="button"
              role="option"
              aria-selected={false}
              disabled={addingToId !== null}
              onClick={() => addToList(list.id)}
              className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-start text-body-sm text-ink-900 outline-none hover:bg-paper-warm focus-visible:shadow-focus disabled:opacity-50"
            >
              <span>{list.name}</span>
              <span className="text-caption text-ink-400">{list.item_count}</span>
            </button>
          ))
        )}

        {showCreate ? (
          <form onSubmit={handleCreate} className="flex flex-col gap-2 p-1">
            <input
              type="text"
              placeholder="List name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={120}
              required
              autoFocus
              className="rounded-sm border border-ink-300 px-2 py-1 text-body-sm text-ink-900 outline-none focus:border-accent focus:shadow-focus"
              aria-label="New list name"
            />
            <div className="flex gap-1">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={createBusy}
                disabled={createBusy || !newName.trim()}
              >
                Create & add
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={createBusy}
                onClick={() => { setShowCreate(false); setNewName(""); }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-start text-body-sm text-accent outline-none hover:bg-accent-soft focus-visible:shadow-focus"
          >
            <PlusGlyph />
            New list
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Save to list"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-body-sm text-ink-600 outline-none hover:bg-paper-warm hover:text-ink-900 focus-visible:shadow-focus"
      >
        <BookmarkGlyph />
        <span>Save to list</span>
      </button>

      {open && typeof document !== "undefined"
        ? createPortal(panel, document.body)
        : null}
    </div>
  );
}

function BookmarkGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2Z" />
    </svg>
  );
}

function PlusGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
